import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact, { type BugsnagErrorBoundary } from '@bugsnag/plugin-react'
import React from 'react'

import { BUGSNAG_API_KEY, ENVNAME } from '~/constants/environment'

import packageJson from '../../package.json'

const isAvailable = ENVNAME !== 'local'

if (isAvailable) {
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    appVersion: packageJson.version,
    releaseStage: ENVNAME,
    plugins: [new BugsnagPluginReact()],
  })
}

export const notify = isAvailable ? Bugsnag.notify : undefined

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React)

type BugSnagProps = Pick<React.ComponentProps<BugsnagErrorBoundary>, 'FallbackComponent'>

type FallbackBoundaryState = {
  error?: Error
}

class FallbackErrorBoundary extends React.Component<
  React.PropsWithChildren<BugSnagProps>,
  FallbackBoundaryState
> {
  state: FallbackBoundaryState = {
    error: undefined,
  }

  static getDerivedStateFromError(error: Error): FallbackBoundaryState {
    return { error }
  }

  render() {
    const { error } = this.state
    const { children, FallbackComponent } = this.props

    if (!error) {
      return children
    }

    if (!FallbackComponent) {
      return null
    }

    return <FallbackComponent error={error} info={{}} clearError={() => {}} />
  }
}

function BugSnag(props: React.PropsWithChildren<BugSnagProps>) {
  if (ErrorBoundary) {
    return <ErrorBoundary {...props} />
  }

  return <FallbackErrorBoundary {...props} />
}

export default BugSnag
